// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { Link } from '@/components/link';
import { SupportSnackbar } from '@/components/support-snackbar';
import { toTransportReferenceDetailsRequest } from '@/constants/shipping-carrier';
import { useToast } from '@/contexts/toast-context';
import { createReturnOrder } from '@/return-order/api/create-return-order';
import { ReturnOrder } from '@/return-order/api/types';
import { updateReturnOrder } from '@/return-order/api/update-return-order';
import { ReturnOrderForm } from '@/return-order/components/return-order-form';
import { useReturnOrderFileUploads } from '@/return-order/hooks/use-return-order-file-uploads';
import {
  returnOrderDetailQueryKey,
  returnOrdersQueryKey,
} from '@/return-order/query-keys';
import { ReturnOrderFormData } from '@/return-order/schemas/return-order-form-schema';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { captureException } from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Fragment, useCallback, useState } from 'react';

interface Props {
  /**
   * Whether the dialog is open.
   */
  isOpen: boolean;

  /**
   * Callback fired when the dialog is closed.
   */
  onClose: () => void;

  /**
   * Optional return order to edit. If provided, the dialog will be in edit mode.
   */
  returnOrder?: ReturnOrder;

  /**
   * Optional initial form data to pre-populate the form.
   * Used for cloning a return order with pre-filled values.
   */
  initialFormData?: ReturnOrderFormData;
}

const FORM_ID = 'return-order-form';

/**
 * Dialog for creating or editing a return order.
 * Handles mutations, error display, and success notifications.
 */
export function ReturnOrderDialog(props: Props) {
  const [formVersion, setFormVersion] = useState(0);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { user } = useCurrentUser();
  const isEditMode = !!props.returnOrder;
  const fileUploads = useReturnOrderFileUploads();

  const createMutation = useMutation({
    mutationFn: createReturnOrder,
    onSuccess: (returnOrder) => {
      queryClient.invalidateQueries({
        queryKey: returnOrdersQueryKey,
        refetchType: 'active',
      });
      showToast({
        element: (
          <Typography>
            Return order{' '}
            <Link
              to="/return-orders/$returnOrderKey"
              params={{ returnOrderKey: returnOrder.key }}
              color="primary.main"
              sx={{ fontWeight: 'bold', textDecoration: 'underline' }}
            >
              {returnOrder.returnOrderNumber}
            </Link>{' '}
            created
          </Typography>
        ),
        severity: 'success',
        duration: 3000,
      });
      props.onClose();
    },
    onError: (error) => {
      const apiError = handleAPIError(error);
      const curated = getUserFacingErrorMessage(apiError);
      if (curated !== SUPPORT_FALLBACK_ERROR_MESSAGE) {
        setErrorMessage(curated);
        setShowSnackbar(false);
      } else {
        if (!apiError.sentToSentry) {
          captureException(error);
        }
        setErrorMessage(null);
        setShowSnackbar(true);
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      returnOrderKey,
      request,
    }: {
      returnOrderKey: string;
      request: Parameters<typeof updateReturnOrder>[1];
    }) => updateReturnOrder(returnOrderKey, request),
    onSuccess: (updatedReturnOrder, variables) => {
      queryClient.setQueryData(
        returnOrderDetailQueryKey(variables.returnOrderKey),
        updatedReturnOrder
      );
      queryClient.invalidateQueries({
        queryKey: returnOrdersQueryKey,
        refetchType: 'active',
      });
      showToast({
        element: (
          <Typography>
            Return order{' '}
            <Link
              to="/return-orders/$returnOrderKey"
              params={{ returnOrderKey: updatedReturnOrder.key }}
              color="primary.main"
              sx={{ fontWeight: 'bold', textDecoration: 'underline' }}
            >
              {updatedReturnOrder.returnOrderNumber}
            </Link>{' '}
            updated
          </Typography>
        ),
        severity: 'success',
        duration: 3000,
      });
      props.onClose();
    },
    onError: (error) => {
      const apiError = handleAPIError(error);
      const curated = getUserFacingErrorMessage(apiError);
      if (curated !== SUPPORT_FALLBACK_ERROR_MESSAGE) {
        setErrorMessage(curated);
        setShowSnackbar(false);
      } else {
        if (!apiError.sentToSentry) {
          captureException(error);
        }
        setErrorMessage(null);
        setShowSnackbar(true);
      }
    },
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  // File upload state affects submit button (only for create mode)
  const canSubmit =
    !isPending &&
    (isEditMode ||
      (!fileUploads.isAnyUploading && !fileUploads.hasFailedUploads));

  const handleAfterClose = useCallback(() => {
    setFormVersion((v) => v + 1);
    setErrorMessage(null);
    setShowSnackbar(false);
    createMutation.reset();
    updateMutation.reset();
    fileUploads.reset();
  }, [createMutation, updateMutation, fileUploads]);

  const handleFormSubmit = useCallback(
    (data: ReturnOrderFormData) => {
      const items = data.items.map((item) => ({
        itemId: item.item?.id ?? '',
        requestedQuantity: item.quantity ?? 0,
      }));

      if (isEditMode && props.returnOrder) {
        const updateRequest = {
          returnType: data.returnType!,
          returnReason: data.returnReason,
          fromLocationId: data.fromLocation?.id ?? null,
          toLocationId: data.toLocation?.id ?? '',
          promiseShipDate: data.promiseShipDate,
          promiseDeliveryDate: data.promiseDeliveryDate,
          orderNotes: data.orderNotes,
          transportReferenceDetails: toTransportReferenceDetailsRequest(
            data.carrier,
            data.otherCarrier,
            data.referenceNumber
          ),
          items,
        };

        updateMutation.mutate({
          returnOrderKey: props.returnOrder.key,
          request: updateRequest,
        });
        return;
      }

      const createRequest = {
        returnType: data.returnType!,
        returnReason: data.returnReason,
        fromLocationId: data.fromLocation?.id ?? null,
        toLocationId: data.toLocation?.id ?? '',
        promiseShipDate: data.promiseShipDate,
        promiseDeliveryDate: data.promiseDeliveryDate,
        orderNotes: data.orderNotes,
        transportReferenceDetails: toTransportReferenceDetailsRequest(
          data.carrier,
          data.otherCarrier,
          data.referenceNumber
        ),
        items,
        fileIds: fileUploads.getCompletedFileIds(),
      };

      createMutation.mutate(createRequest);
    },
    [isEditMode, props.returnOrder, createMutation, updateMutation, fileUploads]
  );

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={props.isOpen}
      onClose={props.onClose}
      scroll="paper"
      TransitionProps={{
        onExited: handleAfterClose,
      }}
    >
      <DialogTitle>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <span>{isEditMode ? 'Edit return order' : 'Add return order'}</span>
          {isEditMode && props.returnOrder?.exchangeGroup && (
            <Typography variant="body2" color="text.secondary">
              Part of exchange{' '}
              <b>{props.returnOrder.exchangeGroup.exchangeNumber}</b>
              {props.returnOrder.exchangeGroup.fulfillmentOrders.length > 0 && (
                <>
                  {' (linked to '}
                  {props.returnOrder.exchangeGroup.fulfillmentOrders.map(
                    (fo, index) => (
                      <Fragment key={fo.key}>
                        {index > 0 && ', '}
                        <Link
                          to="/fulfillments/$fulfillmentKey"
                          params={{ fulfillmentKey: fo.key }}
                          target="_blank"
                        >
                          {fo.fulfillmentOrderNumber}
                        </Link>
                      </Fragment>
                    )
                  )}
                  {')'}
                </>
              )}
            </Typography>
          )}
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {errorMessage && (
          <Alert
            severity="error"
            onClose={() => setErrorMessage(null)}
            sx={{ mb: 2 }}
          >
            {errorMessage}
          </Alert>
        )}
        <ReturnOrderForm
          key={formVersion}
          returnOrder={props.returnOrder}
          initialFormData={props.initialFormData}
          orgDefaultReturnLocation={user.organization.defaultReturnLocation}
          onSubmit={handleFormSubmit}
          formId={FORM_ID}
          fileUploadProps={
            isEditMode
              ? undefined
              : {
                  files: fileUploads.files,
                  onFilesSelected: fileUploads.addFiles,
                  onRemoveFile: fileUploads.removeFile,
                  disabled: isPending,
                }
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose}>Cancel</Button>
        <Button
          type="submit"
          form={FORM_ID}
          variant="contained"
          color="primary"
          disabled={!canSubmit}
          startIcon={
            isPending || fileUploads.isAnyUploading ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          {isEditMode ? 'Save changes' : 'Create return order'}
        </Button>
      </DialogActions>
      <SupportSnackbar
        isOpen={showSnackbar}
        onClose={() => {
          setShowSnackbar(false);
          setErrorMessage(null);
          createMutation.reset();
          updateMutation.reset();
        }}
        message={
          isEditMode
            ? 'An error occurred updating the return order.'
            : 'An error occurred creating the return order.'
        }
      />
    </Dialog>
  );
}
